import Phaser from 'phaser';

import * as ABCya from '@abcya/abcya_phaser';
import SoundAssetKeys from './SoundAssetKeys';

/**
 * A Cube Button has 3 sides - top/left/right. The origin of those images (in the TexturePacker export) must be
 * set to the shared corner (the middle point where all 3 sides intersect). The (0,0) position of the CubeButton container
 * is the intersection of those three corners. When positioning a CubeButton, the position it is placed will be where the 
 * 3 corners will end up being.
 */
export default class CubeButton extends Phaser.GameObjects.Container
{
    /**
     * Create a Cube button
     * @param {Phaser.Scene} scene the owner scene
     * @param {string} topKey the top button image in UIAssetKeys
     * @param {string} leftKey the left button image in UIAssetKeys
     * @param {string} rightKey the right button image in UIAssetKeys
     */
    constructor(scene, topKey, leftKey, rightKey)
    {
        super(scene);

        this.scene.add.existing(this);

        this._setup(topKey, leftKey, rightKey);
    }

    /**
     * Setup the button graphics and event listeners.
     * @param {string} topKey the top button image in UIAssetKeys
     * @param {string} leftKey the left button image in UIAssetKeys
     * @param {string} rightKey the right button image in UIAssetKeys
     */
    _setup(topKey, leftKey, rightKey)
    {
        this.topSide = ABCya.Images.addImage(0, 0, topKey, this);
        this.leftSide = ABCya.Images.addImage(0, 0, leftKey, this);
        this.rightSide = ABCya.Images.addImage(0, 0, rightKey, this);

        this.topSide.normalPosition = { x: this.topSide.x, y: this.topSide.y };
        this.leftSide.normalPosition = { x: this.leftSide.x, y: this.leftSide.y };
        this.rightSide.normalPosition = { x: this.rightSide.x, y: this.rightSide.y };

        this.setSize(this.leftSide.width + this.rightSide.width, this.topSide.height * 2);
        this.setInteractive({ useHandCursor: true });

        // for debugging
        //this.add(this.scene.add.rectangle(0, 0, this.leftSide.width + this.rightSide.width, this.topSide.height * 2, 0xff0000, 0.5));
        //this.scene.input.enableDebug(this);

        this.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OVER, (pointer) => 
        {
            if (this.input.enabled)
            {
                // expand them a bit
                this.scene.tweens.killTweensOf([this.topSide, this.rightSide, this.leftSide]);
                ABCya.Tweens.addTween(this.topSide, 400, { y: this.topSide.normalPosition.y - 5 }, Phaser.Math.Easing.Back.Out);
                ABCya.Tweens.addTween(this.leftSide, 400, { x: this.leftSide.normalPosition.x - 5 }, Phaser.Math.Easing.Back.Out);
                ABCya.Tweens.addTween(this.rightSide, 400, { x: this.rightSide.normalPosition.x + 5 }, Phaser.Math.Easing.Back.Out);

                if (pointer.event.type == "mousemove") ABCya.Audio.playOneShot(SoundAssetKeys._button_tween, this.scene);
            }
        });
        this.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OUT, () => 
        {
            // reset back to original positions; also reset scale and tint in case the pointer is down
            if (this.input.enabled)
            {
                this.scene.tweens.killTweensOf([this.topSide, this.rightSide, this.leftSide]);
                ABCya.Tweens.addTween(this.topSide, 400, { y: this.topSide.normalPosition.y, scale: 1, tint: 0xffffff }, Phaser.Math.Easing.Back.In);
                ABCya.Tweens.addTween(this.leftSide, 400, { x: this.leftSide.normalPosition.x, scale: 1, tint: 0xffffff }, Phaser.Math.Easing.Back.In);
                ABCya.Tweens.addTween(this.rightSide, 400, { x: this.rightSide.normalPosition.x, scale: 1, tint: 0xffffff }, Phaser.Math.Easing.Back.In);
            }
        });
        this.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, (pointer) => 
        {
            if (this.input.enabled && pointer.button == 0)
            {
                // shrink and tint; also set to expanded positions
                this.scene.tweens.killTweensOf([this.topSide, this.rightSide, this.leftSide]);
                ABCya.Tweens.addTween(this.topSide, 200, { y: this.topSide.normalPosition.y - 5, scale: 0.9, tint: 0xbbbbbb }, Phaser.Math.Easing.Quadratic.Out);
                ABCya.Tweens.addTween(this.leftSide, 200, { x: this.leftSide.normalPosition.x - 5, scale: 0.9, tint: 0xbbbbbb }, Phaser.Math.Easing.Quadratic.Out);
                ABCya.Tweens.addTween(this.rightSide, 200, { x: this.rightSide.normalPosition.x + 5, scale: 0.9, tint: 0xbbbbbb }, Phaser.Math.Easing.Quadratic.Out);
            }
        });
        this.on(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, (pointer) => 
        {
            if (this.input.enabled && pointer.button == 0)
            {
                // reset shrink and tint; also set to expanded positions
                this.scene.tweens.killTweensOf([this.topSide, this.rightSide, this.leftSide]);
                ABCya.Tweens.addTween(this.topSide, 200, { y: this.topSide.normalPosition.y, scale: 1, tint: 0xffffff }, Phaser.Math.Easing.Quadratic.InOut);
                ABCya.Tweens.addTween(this.leftSide, 200, { x: this.leftSide.normalPosition.x, scale: 1, tint: 0xffffff }, Phaser.Math.Easing.Quadratic.InOut);
                ABCya.Tweens.addTween(this.rightSide, 200, { x: this.rightSide.normalPosition.x, scale: 1, tint: 0xffffff }, Phaser.Math.Easing.Quadratic.InOut);

                ABCya.Audio.playOneShot(SoundAssetKeys._click, this.scene);

                this.emit(CubeButtonEvents.CLICKED);
            }
        });
    }

    /**
     * Disables the cube button from interaction.
     */
    disableInteractive()
    {
        super.disableInteractive();

        this.scene.tweens.preUpdate();
        this.scene.tweens.killTweensOf([this.topSide, this.rightSide, this.leftSide]);

        ABCya.Tweens.addTween(this.topSide, 100, { y: this.topSide.normalPosition.y, scale: 1, tint: 0xffffff }, Phaser.Math.Easing.Quadratic.InOut);
        ABCya.Tweens.addTween(this.leftSide, 100, { x: this.leftSide.normalPosition.x, scale: 1, tint: 0xffffff }, Phaser.Math.Easing.Quadratic.InOut);
        ABCya.Tweens.addTween(this.rightSide, 100, { x: this.rightSide.normalPosition.x, scale: 1, tint: 0xffffff }, Phaser.Math.Easing.Quadratic.InOut);
    }
}

/**
 * Cube Button events.
 */
export const CubeButtonEvents = {
    CLICKED: "clicked"
}